import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Shield, MapPin, Clock, CheckCircle2, AlertOctagon, Heart } from 'lucide-react';
import { api } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';

/**
 * Public live tracker for emergency contacts.
 *
 * Read-only view loaded from /safety/track/:token. Shows the provider's
 * next appointment, redacted client first name, postcode-area only,
 * latest check-in status, and an embedded OpenStreetMap pin centred on
 * the postcode-area centroid. No login. Possession of the token is
 * the auth.
 */
export default function SafetyTracker() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Ack state: localStorage-backed so a returning visitor keeps
  // their confirmation even if the API count includes lots of people.
  const ackKey = `zuriya_safety_ack_${token}`;
  const [ackName, setAckName] = useState('');
  const [ackNote, setAckNote] = useState('');
  const [acking, setAcking] = useState(false);
  const [myAck, setMyAck] = useState(() => {
    try { return JSON.parse(localStorage.getItem(ackKey) || 'null'); }
    catch { return null; }
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/safety/track/${token}`);
      setData(res.data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 410) setError('expired');
      else if (status === 404) setError('notfound');
      else setError('unknown');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleAck = async () => {
    if (!ackName.trim()) {
      toast.error('Please add your name so the provider knows who checked in.');
      return;
    }
    setAcking(true);
    try {
      const res = await api.post(`/safety/track/${token}/contact-made`, {
        name: ackName.trim(),
        note: ackNote.trim() || undefined,
      });
      const ack = { name: ackName.trim(), note: ackNote.trim() || null, ack_id: res.data?.ack_id, ack_at: new Date().toISOString() };
      try { localStorage.setItem(ackKey, JSON.stringify(ack)); } catch { /* private mode, ignore */ }
      setMyAck(ack);
      toast.success('Thank you. The provider knows you\u2019ve made contact.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not record your check-in. Please try again.');
    } finally { setAcking(false); }
  };

  // Refresh every 60s so the check-in status updates as the
  // provider taps through. Cheap, single endpoint, no auth.
  useEffect(() => {
    if (!data) return;
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [data, load]);

  /** Compact relative-time helper for the ack timeline. */
  const relTime = (iso) => {
    if (!iso) return '';
    const ms = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(ms) || ms < 0) return 'just now';
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center px-4">
        <p className="text-sm text-stone-500">Loading appointment status&hellip;</p>
      </div>
    );
  }

  if (error) {
    const messages = {
      expired: { title: 'This link has expired', body: 'The provider can issue a fresh link from their dashboard if they want to share again.' },
      notfound: { title: 'Link not found', body: 'Double-check the URL you were sent. The link may have been revoked.' },
      unknown: { title: 'Something went wrong', body: 'Please try again in a moment.' },
    };
    const m = messages[error];
    return (
      <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center px-4" data-testid="safety-tracker-error">
        <div className="max-w-md w-full text-center bg-white border border-[#E6E4DD] rounded-lg p-8">
          <Shield className="h-10 w-10 text-stone-300 mx-auto mb-3" />
          <h1 className="font-heading text-xl text-[#1A1A1A] mb-2">{m.title}</h1>
          <p className="text-sm text-stone-500 mb-4">{m.body}</p>
          <Link to="/" className="text-xs text-[#D4AF37] hover:underline">Back to Zuriya</Link>
        </div>
      </div>
    );
  }

  const checkin = data?.latest_checkin;
  const coords = data?.location_coords;
  // OSM expects bbox=lon-d,lat-d,lon+d,lat+d. ~0.06 deg ~= 4-6 mile box.
  const bbox = coords
    ? `${coords.longitude - 0.06},${coords.latitude - 0.04},${coords.longitude + 0.06},${coords.latitude + 0.04}`
    : null;
  const mapSrc = bbox
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${coords.latitude},${coords.longitude}`
    : null;

  const statusPill = checkin ? (() => {
    const map = {
      confirmed: { tone: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: `${checkin.type === 'arrival' ? 'Arrived safely' : 'Finished safely'}` },
      pending:   { tone: 'bg-amber-50 text-amber-700 border-amber-200',       label: `${checkin.type === 'arrival' ? 'Arriving soon' : 'Wrapping up soon'}` },
      missed:    { tone: 'bg-orange-50 text-orange-700 border-orange-200',    label: 'Awaiting check-in' },
      escalated: { tone: 'bg-red-50 text-red-700 border-red-200',             label: 'Check-in escalated' },
    };
    return map[checkin.status] || { tone: 'bg-stone-100 text-stone-600 border-stone-200', label: checkin.status };
  })() : null;

  return (
    <div className="min-h-screen bg-[#F9F6F0]" data-testid="safety-tracker">
      <SEO title="Live appointment status" description="Live appointment status from Zuriya safety share." />
      <header className="bg-[#1A1A1A] py-4 px-4">
        <div className="max-w-2xl mx-auto flex items-center gap-2">
          <Shield className="h-5 w-5 text-[#D4AF37]" />
          <span className="text-white font-heading text-lg">Zuriya Safety</span>
          <span className="ml-auto text-[10px] text-stone-400 uppercase tracking-widest">Live status</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        {data.mode === 'quiet' && (
          <p className="text-[11px] text-stone-500 bg-white border border-[#E6E4DD] inline-flex items-center gap-1 px-2 py-1 rounded-full" data-testid="tracker-mode-quiet">
            <Shield className="h-3 w-3 text-[#D4AF37]" />
            Quiet share, appointment timing only
          </p>
        )}
        <section className="bg-white border border-[#E6E4DD] rounded-lg p-5" data-testid="tracker-summary">
          <p className="text-xs text-stone-500 uppercase tracking-widest mb-1">Provider</p>
          <h1 className="font-heading text-2xl text-[#1A1A1A]">{data.provider_name}</h1>
          <p className="text-sm text-stone-600 mt-1">
            {data.service_name} with {data.client_first_name}
          </p>
          <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
            <div className="bg-[#F9F6F0] rounded p-3">
              <p className="text-stone-500 flex items-center gap-1"><Clock className="h-3 w-3" />When</p>
              <p className="text-[#1A1A1A] font-medium mt-1">{data.date} at {data.time}</p>
              {data.duration_minutes && (
                <p className="text-stone-500 mt-0.5">~{data.duration_minutes} min</p>
              )}
            </div>
            <div className="bg-[#F9F6F0] rounded p-3">
              <p className="text-stone-500 flex items-center gap-1"><MapPin className="h-3 w-3" />Area</p>
              <p className="text-[#1A1A1A] font-medium mt-1">{data.location_area || (data.mode === 'quiet' ? 'Hidden' : 'Not specified')}</p>
              <p className="text-stone-500 mt-0.5">{data.mode === 'quiet' ? 'Quiet share' : 'Postcode prefix only'}</p>
            </div>
          </div>
        </section>

        {statusPill && (
          <section className="bg-white border border-[#E6E4DD] rounded-lg p-5" data-testid="tracker-status">
            <p className="text-xs text-stone-500 uppercase tracking-widest mb-2">Latest check-in</p>
            <div className="flex items-center gap-2">
              {checkin.status === 'confirmed' ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              ) : checkin.status === 'escalated' ? (
                <AlertOctagon className="h-5 w-5 text-red-500" />
              ) : (
                <Clock className="h-5 w-5 text-amber-500" />
              )}
              <span className={`px-2 py-1 rounded-full text-xs font-medium border ${statusPill.tone}`}>
                {statusPill.label}
              </span>
            </div>
            <p className="text-xs text-stone-500 mt-2">
              {checkin.confirmed_at
                ? `Confirmed at ${new Date(checkin.confirmed_at).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}`
                : `Scheduled for ${new Date(checkin.scheduled_for).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}`}
            </p>
          </section>
        )}

        {mapSrc ? (
          <section className="bg-white border border-[#E6E4DD] rounded-lg overflow-hidden" data-testid="tracker-map">
            <div className="px-5 py-3 border-b border-[#E6E4DD] flex items-center justify-between">
              <p className="text-xs text-stone-500 uppercase tracking-widest flex items-center gap-1">
                <MapPin className="h-3 w-3" />Approximate area
              </p>
              <a
                href={`https://www.openstreetmap.org/?mlat=${coords.latitude}&mlon=${coords.longitude}#map=14/${coords.latitude}/${coords.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] text-[#D4AF37] hover:underline"
                data-testid="tracker-map-open"
              >
                Open in OpenStreetMap
              </a>
            </div>
            <iframe
              title="Appointment area map"
              src={mapSrc}
              className="w-full h-72 border-0"
              loading="lazy"
            />
            <p className="px-5 py-2 text-[10px] text-stone-400 bg-[#F9F6F0]">
              Pin shows the centre of the postcode area, not the client&rsquo;s door.
            </p>
          </section>
        ) : (
          <section className="bg-white border border-[#E6E4DD] rounded-lg p-5 text-center" data-testid="tracker-map-fallback">
            <MapPin className="h-6 w-6 text-stone-300 mx-auto mb-2" />
            <p className="text-xs text-stone-500">Map unavailable for this appointment area.</p>
          </section>
        )}

        <section className="bg-white border border-[#E6E4DD] rounded-lg p-5" data-testid="tracker-ack-section">
          <div className="flex items-center gap-2 mb-1">
            <Heart className="h-4 w-4 text-[#D4AF37]" />
            <p className="text-sm font-medium text-[#1A1A1A]">I&rsquo;ve made contact</p>
          </div>
          <p className="text-xs text-stone-500 mb-3">
            Tap this once you&rsquo;ve heard from {data.provider_name.split(' ')[0]}. It tells them you&rsquo;ve checked in and helps the Zuriya safety team close out any open follow-up faster.
          </p>
          {data.contact_acks_count > 0 && (
            <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 inline-block px-2 py-0.5 rounded-full mb-3" data-testid="tracker-ack-count">
              {data.contact_acks_count} {data.contact_acks_count === 1 ? 'person has' : 'people have'} already checked in
            </p>
          )}
          {data.contact_acks_recent && data.contact_acks_recent.length > 0 && (
            <ul className="mb-3 space-y-1" data-testid="tracker-ack-timeline">
              {data.contact_acks_recent.map((a, i) => (
                <li
                  key={`${a.ack_at}-${i}`}
                  className="flex items-center gap-2 text-xs text-stone-600"
                  data-testid={`tracker-ack-row-${i}`}
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                  <span className="font-medium text-[#1A1A1A]">{a.name}</span>
                  <span className="text-stone-400">checked in</span>
                  <span className="text-stone-400">{relTime(a.ack_at)}</span>
                </li>
              ))}
            </ul>
          )}
          {myAck ? (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="tracker-ack-confirmed">
              <p className="text-sm font-medium text-emerald-800 flex items-center gap-1">
                <CheckCircle2 className="h-4 w-4" />Thanks, {myAck.name}.
              </p>
              <p className="text-xs text-emerald-700 mt-1">
                We&rsquo;ve let {data.provider_name.split(' ')[0]} know you&rsquo;ve made contact{myAck.ack_at ? ` (${new Date(myAck.ack_at).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })})` : ''}.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Input
                value={ackName}
                onChange={(e) => setAckName(e.target.value)}
                placeholder="Your name"
                className="input-zuriya"
                data-testid="tracker-ack-name-input"
                maxLength={60}
              />
              <Input
                value={ackNote}
                onChange={(e) => setAckNote(e.target.value)}
                placeholder="Optional note (e.g. spoke at 3pm, all good)"
                className="input-zuriya"
                data-testid="tracker-ack-note-input"
                maxLength={280}
              />
              <Button
                onClick={handleAck}
                disabled={acking}
                className="btn-primary w-full gap-1"
                data-testid="tracker-ack-btn"
              >
                <Heart className="h-3.5 w-3.5" />I&rsquo;ve made contact
              </Button>
            </div>
          )}
        </section>

        <section className="text-center text-[10px] text-stone-400 py-4">
          <p>
            Link expires {data.expires_at ? new Date(data.expires_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'soon'}.
          </p>
          <p className="mt-1">If you can&rsquo;t reach the provider and you&rsquo;re worried, call them. If they&rsquo;re unreachable, call 999 in the UK.</p>
        </section>
      </main>
    </div>
  );
}
